import type { FC } from "react"
import React from "react"

import { wrapInto } from "@logic/lib/utils"
import { useRecoilValue, useRecoilState } from "recoil"

import {
  flawedAreaIndex,
  focusedFlawIndexSoft,
} from "~/containers/LegacyDesigner/state/flaws"
import { areWeAfterPhase } from "~/containers/LegacyDesigner/state/phase"
import { selectedInstallAreaIdState } from "~/containers/Proposal/state/designs/installAreas"

import { DirectionIndicator } from "./DirectionIndicator"
import { InsetArea } from "./InsetArea"
import { findProjectedAreaState } from "../../../state/elements"
import { CoordinateTooltip } from "../../Overlays/CoordinateTooltip"

const COLORS = [
  `#f0f`,
  `#0f0`,
  `#0ff`,
  `#f00`,
  `#ff0`,
  `#fa0`,
  `#00f`,
  `#a0f`,
  `#aaa`,
]

const InstallArea_Internal: FC<{ id: string; idx: number }> = ({ id, idx }) => {
  const { nodes, azimuth } = useRecoilValue(findProjectedAreaState(id))
  const [selectedId, setSelectedId] = useRecoilState(selectedInstallAreaIdState)

  const hasSelected = !!selectedId
  const isSelected = selectedId === id
  const opacityModifier = isSelected ? 5 : hasSelected ? 1 : 2
  const focusedFlawIds = useRecoilValue(focusedFlawIndexSoft)

  const isAfterAttributes = useRecoilValue(areWeAfterPhase(`Roof Attributes`))

  const flawedIds = useRecoilValue(flawedAreaIndex)
  const isInvalid = flawedIds.includes(id)
  const isShownInvalid = isInvalid && focusedFlawIds.includes(id)

  let top = Infinity,
    bottom = 0,
    left = Infinity,
    right = 0

  const joinedPoints = nodes
      .map(({ x, y }) => {
        top = Math.min(top, y)
        bottom = Math.max(bottom, y)
        left = Math.min(left, x)
        right = Math.max(right, x)

        return `${x},${y}`
      })
      .join(` `),
    r = Math.round(azimuth ?? 0) + 180,
    x = left,
    y = top,
    w = right - left,
    h = bottom - top,
    c = { x: x + w / 2, y: y + h / 2 },
    color = wrapInto(idx, COLORS)

  return (
    <>
      <polygon
        points={joinedPoints}
        fill={isAfterAttributes ? `none` : color + opacityModifier}
        data-testid={`area-${id}`}
      />
      {/* <rect x={x} y={y} width={w} height={h} fill="none" stroke="black" /> */}
      {azimuth === null ? null : (
        <DirectionIndicator c={c} r={r} h={20} w={20} weight={2} />
      )}
      <polygon
        onClick={() => setSelectedId(id)}
        points={joinedPoints}
        fill="transparent"
      />
      <InsetArea id={id} idx={idx} />
      {isShownInvalid && <CoordinateTooltip says={id} where={c} />}
    </>
  )
}

export const InstallArea = React.memo(InstallArea_Internal)
